import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Archive, ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';

import { adminContent } from 'services/api';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { LoadingAnimation } from 'components/ui/loading-animation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useToast } from 'components/ui/use-toast';
import type { AdminQuestion } from 'pages/Admin/QuestionLibrary/types';
import type {
  RadarDefinition,
  RadarDefinitionDraft,
  RadarDefinitionDraftAxis,
  RadarDefinitionRevision,
  RadarSourceQuestion,
} from './types';

const SYSTEM_DEFINITION_KEY = 'intake-pillars';
const DEFAULT_RECENT_RESPONSE_COUNT = 3;
const MAX_RECENT_RESPONSE_COUNT = 20;

const emptyAxis = (order: number): RadarDefinitionDraftAxis => ({
  key: '',
  label: '',
  order,
  source_questions: [],
  persisted: false,
});

const emptyDraft = (): RadarDefinitionDraft => ({
  key: '',
  name: '',
  is_active: true,
  recent_response_count: DEFAULT_RECENT_RESPONSE_COUNT,
  axes: [emptyAxis(0), emptyAxis(1), emptyAxis(2)],
});

const toDraft = (definition: RadarDefinition): RadarDefinitionDraft => ({
  ...definition,
  axes: definition.axes
    .slice()
    .sort((left, right) => left.order - right.order)
    .map((axis, order) => ({ ...axis, order, persisted: true })),
});

const responseItems = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown }).results)) {
    return (data as { results: T[] }).results;
  }
  return [];
};

const sourceFromQuestion = (question: AdminQuestion): RadarSourceQuestion => ({
  key: question.key,
  text: question.text,
  is_active: question.is_active !== false,
  flows: (question.flows ?? []).map((flow) => ({
    flow_id: flow.flow_id,
    flow_name: flow.flow_name,
  })),
});

const apiErrorMessage = (error: any) => {
  const data = error?.response?.data;
  if (typeof data?.detail === 'string') {
    return data.detail;
  }
  if (data && typeof data === 'object') {
    return JSON.stringify(data);
  }
  return 'Unable to save the radar profile.';
};

type ScoringDefinitionsProps = {
  initialDefinitionKey?: string | null;
  onDefinitionChange: (key: string | null) => void;
  onBack: () => void;
};

export const ScoringDefinitions: React.FC<ScoringDefinitionsProps> = ({
  initialDefinitionKey,
  onDefinitionChange,
  onBack,
}) => {
  const { addToast } = useToast();
  const initialDefinitionKeyRef = useRef(initialDefinitionKey);
  const onDefinitionChangeRef = useRef(onDefinitionChange);
  const [definitions, setDefinitions] = useState<RadarDefinition[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(
    initialDefinitionKey ?? null,
  );
  const [draft, setDraft] = useState<RadarDefinitionDraft>(emptyDraft);
  const [baseline, setBaseline] = useState<RadarDefinitionDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<RadarDefinitionRevision[]>([]);
  const [activeSourceAxis, setActiveSourceAxis] = useState<number | null>(null);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceCandidates, setSourceCandidates] = useState<AdminQuestion[]>([]);
  const [sourceLoading, setSourceLoading] = useState(false);

  useEffect(() => {
    onDefinitionChangeRef.current = onDefinitionChange;
  }, [onDefinitionChange]);

  const loadDefinitions = useCallback(async (preferredKey?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminContent.radarDefinitions.list();
      const nextDefinitions = responseItems<RadarDefinition>(response.data);
      setDefinitions(nextDefinitions);
      const nextKey =
        preferredKey && nextDefinitions.some((item) => item.key === preferredKey)
          ? preferredKey
          : nextDefinitions.find((item) => item.is_active)?.key ?? nextDefinitions[0]?.key ?? null;
      setSelectedKey(nextKey);
      onDefinitionChangeRef.current(nextKey);
      if (nextKey) {
        const selected = nextDefinitions.find((item) => item.key === nextKey);
        if (selected) {
          const nextDraft = toDraft(selected);
          setDraft(nextDraft);
          setBaseline(nextDraft);
        }
      } else {
        setDraft(emptyDraft());
        setBaseline(null);
      }
    } catch (loadError) {
      console.error('Failed to load scoring definitions', loadError);
      setError('Unable to load radar profiles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDefinitions(initialDefinitionKeyRef.current);
  }, [loadDefinitions]);

  useEffect(() => {
    if (!selectedKey) {
      setHistory([]);
      return;
    }
    void adminContent.radarDefinitions
      .history(selectedKey)
      .then((response) => setHistory(responseItems<RadarDefinitionRevision>(response.data)))
      .catch((historyError) => {
        console.error('Failed to load scoring definition history', historyError);
        setHistory([]);
      });
  }, [selectedKey]);

  const selectDefinition = (definition: RadarDefinition) => {
    const nextDraft = toDraft(definition);
    setSelectedKey(definition.key);
    setDraft(nextDraft);
    setBaseline(nextDraft);
    setActiveSourceAxis(null);
    onDefinitionChange(definition.key);
  };

  const startNew = () => {
    setSelectedKey(null);
    setDraft(emptyDraft());
    setBaseline(null);
    setHistory([]);
    setActiveSourceAxis(null);
    onDefinitionChange(null);
  };

  const updateAxis = (index: number, updates: Partial<RadarDefinitionDraftAxis>) => {
    setDraft((current) => ({
      ...current,
      axes: current.axes.map((axis, axisIndex) =>
        axisIndex === index ? { ...axis, ...updates } : axis,
      ),
    }));
  };

  const normalizeAxisOrder = (axes: RadarDefinitionDraftAxis[]) =>
    axes.map((axis, order) => ({ ...axis, order }));

  const moveAxis = (index: number, direction: -1 | 1) => {
    setDraft((current) => {
      const destination = index + direction;
      if (destination < 0 || destination >= current.axes.length) {
        return current;
      }
      const axes = current.axes.slice();
      [axes[index], axes[destination]] = [axes[destination], axes[index]];
      return { ...current, axes: normalizeAxisOrder(axes) };
    });
  };

  const removeAxis = (index: number) => {
    setDraft((current) => ({
      ...current,
      axes: normalizeAxisOrder(current.axes.filter((_, axisIndex) => axisIndex !== index)),
    }));
    setActiveSourceAxis(null);
  };

  const fetchSourceCandidates = useCallback(async () => {
    setSourceLoading(true);
    try {
      const response = await adminContent.questions.list({
        question_type: 'rating',
        include_inactive: true,
        search: sourceSearch.trim() || undefined,
        page_size: 50,
      });
      setSourceCandidates(responseItems<AdminQuestion>(response.data));
    } catch (candidateError) {
      console.error('Failed to load rating questions', candidateError);
      setSourceCandidates([]);
    } finally {
      setSourceLoading(false);
    }
  }, [sourceSearch]);

  useEffect(() => {
    if (activeSourceAxis !== null) {
      void fetchSourceCandidates();
    }
  }, [activeSourceAxis, fetchSourceCandidates]);

  const mappedQuestionKeys = useMemo(
    () => new Set(draft.axes.flatMap((axis) => axis.source_questions.map((question) => question.key))),
    [draft.axes],
  );

  const addSourceQuestion = (axisIndex: number, question: AdminQuestion) => {
    if (mappedQuestionKeys.has(question.key)) {
      return;
    }
    updateAxis(axisIndex, {
      source_questions: [
        ...draft.axes[axisIndex].source_questions,
        sourceFromQuestion(question),
      ],
    });
  };

  const removeSourceQuestion = (axisIndex: number, questionKey: string) => {
    updateAxis(axisIndex, {
      source_questions: draft.axes[axisIndex].source_questions.filter(
        (question) => question.key !== questionKey,
      ),
    });
  };

  const readinessIssues = useMemo(() => {
    const issues: string[] = [];
    if (!draft.key.trim()) issues.push('Profile key is required.');
    if (!draft.name.trim()) issues.push('Profile name is required.');
    if (
      !Number.isInteger(draft.recent_response_count) ||
      draft.recent_response_count < 1 ||
      draft.recent_response_count > MAX_RECENT_RESPONSE_COUNT
    ) {
      issues.push(
        `Recent responses per question must be between 1 and ${MAX_RECENT_RESPONSE_COUNT}.`,
      );
    }
    if (draft.axes.length < 3) issues.push('At least three axes are required.');
    draft.axes.forEach((axis, index) => {
      if (!axis.key.trim()) issues.push(`Axis ${index + 1} needs a key.`);
      if (!axis.label.trim()) issues.push(`Axis ${index + 1} needs a label.`);
      if (axis.source_questions.length === 0) {
        issues.push(`Axis ${index + 1} needs a source question.`);
      }
    });
    const keys = draft.axes.map((axis) => axis.key.trim()).filter(Boolean);
    if (new Set(keys).size !== keys.length) issues.push('Axis keys must be unique.');
    return issues;
  }, [draft]);

  const structureSignature = (value: RadarDefinitionDraft | null) =>
    JSON.stringify(
      {
        recent_response_count: value?.recent_response_count ?? null,
        axes:
          value?.axes.map((axis) => ({
            key: axis.key,
            order: axis.order,
            sources: axis.source_questions.map((question) => question.key).sort(),
          })) ?? [],
      },
    );

  const handleSave = async () => {
    if (readinessIssues.length > 0) {
      addToast({
        title: 'Radar profile is not ready',
        description: readinessIssues[0],
        variant: 'destructive',
      });
      return;
    }
    if (
      selectedKey &&
      structureSignature(baseline) !== structureSignature(draft) &&
      !window.confirm(
        'Changing the recent-response window, axes, or source questions recalculates historical radar values. Continue?',
      )
    ) {
      return;
    }

    const payload = {
      key: draft.key.trim(),
      name: draft.name.trim(),
      recent_response_count: draft.recent_response_count,
      axes: draft.axes.map((axis, order) => ({
        key: axis.key.trim(),
        label: axis.label.trim(),
        order,
        source_question_keys: axis.source_questions.map((question) => question.key),
      })),
    };

    setSaving(true);
    try {
      const response = selectedKey
        ? await adminContent.radarDefinitions.update(selectedKey, payload)
        : await adminContent.radarDefinitions.create(payload);
      const saved = response.data as RadarDefinition;
      addToast({ title: 'Radar profile saved', description: saved.name });
      await loadDefinitions(saved.key);
    } catch (saveError) {
      console.error('Failed to save scoring definition', saveError);
      addToast({
        title: 'Unable to save radar profile',
        description: apiErrorMessage(saveError),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedKey || selectedKey === SYSTEM_DEFINITION_KEY) {
      return;
    }
    if (!window.confirm('Archive this radar profile? Existing charts will stop loading it.')) {
      return;
    }
    setSaving(true);
    try {
      await adminContent.radarDefinitions.archive(selectedKey);
      addToast({ title: 'Radar profile archived' });
      await loadDefinitions(null);
    } catch (archiveError) {
      addToast({
        title: 'Unable to archive radar profile',
        description: apiErrorMessage(archiveError),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[24rem] items-center justify-center">
        <LoadingAnimation />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scoring-definitions-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Radar Chart Setup</h2>
          <p className="text-sm text-muted-foreground">
            Choose which rating questions contribute to each radar chart axis.
          </p>
        </div>
        <Button type="button" variant="ghost" size="auto" onClick={onBack}>
          Back to questions
        </Button>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <Card maxWidth="none" data-testid="scoring-definition-editor-card">
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <CardTitle>
              {selectedKey ? `Edit ${draft.name}` : 'Create radar profile'}
            </CardTitle>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-end lg:w-auto">
              <div className="min-w-0 flex-1 space-y-1 lg:w-80">
                <Label htmlFor="radar-definition-selector">Radar profile</Label>
                <Select
                  value={selectedKey ?? ''}
                  onValueChange={(key) => {
                    const definition = definitions.find((item) => item.key === key);
                    if (definition) selectDefinition(definition);
                  }}
                  disabled={definitions.length === 0}
                >
                  <SelectTrigger
                    id="radar-definition-selector"
                    aria-label="Radar profile"
                    className="w-full"
                  >
                    <SelectValue placeholder="Choose a radar profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {definitions.map((definition) => (
                      <SelectItem key={definition.key} value={definition.key}>
                        {definition.name}{definition.is_active ? '' : ' (Archived)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" size="auto" onClick={startNew}>
                <Plus className="mr-2 h-4 w-4" /> New profile
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="items-stretch space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="radar-definition-key">Profile key</Label>
                  <Input
                    id="radar-definition-key"
                    value={draft.key}
                    disabled={Boolean(selectedKey)}
                    onChange={(event) => setDraft((current) => ({ ...current, key: event.target.value }))}
                    placeholder="wellbeing-profile"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="radar-definition-name">Profile name</Label>
                  <Input
                    id="radar-definition-name"
                    value={draft.name}
                    onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                    placeholder="Wellbeing Profile"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="radar-definition-recent-response-count">
                    Recent responses per question
                  </Label>
                  <Input
                    id="radar-definition-recent-response-count"
                    type="number"
                    min={1}
                    max={MAX_RECENT_RESPONSE_COUNT}
                    step={1}
                    value={draft.recent_response_count}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        recent_response_count: Number(event.target.value),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Each source question uses this many of its latest valid answers.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-foreground">Axes and source questions</h3>
                    <p className="text-xs text-muted-foreground">
                      Axis keys are permanent after the radar profile is saved.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="auto"
                    onClick={() =>
                      setDraft((current) => ({
                        ...current,
                        axes: [...current.axes, emptyAxis(current.axes.length)],
                      }))
                    }
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add axis
                  </Button>
                </div>

                {draft.axes.map((axis, axisIndex) => (
                  <div
                    key={`${axis.key || 'new-axis'}-${axisIndex}`}
                    className="space-y-4 rounded-xl border border-border/50 bg-white/30 p-4"
                  >
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="grid min-w-0 flex-1 gap-3 md:grid-cols-2">
                        <div className="space-y-1">
                          <Label htmlFor={`radar-axis-key-${axisIndex}`}>Axis key</Label>
                          <Input
                            id={`radar-axis-key-${axisIndex}`}
                            value={axis.key}
                            disabled={axis.persisted}
                            onChange={(event) => updateAxis(axisIndex, { key: event.target.value })}
                            placeholder="mindset"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`radar-axis-label-${axisIndex}`}>Display label</Label>
                          <Input
                            id={`radar-axis-label-${axisIndex}`}
                            value={axis.label}
                            onChange={(event) => updateAxis(axisIndex, { label: event.target.value })}
                            placeholder="Mindset"
                          />
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => moveAxis(axisIndex, -1)}
                          disabled={axisIndex === 0}
                          aria-label={`Move axis ${axisIndex + 1} up`}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => moveAxis(axisIndex, 1)}
                          disabled={axisIndex === draft.axes.length - 1}
                          aria-label={`Move axis ${axisIndex + 1} down`}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAxis(axisIndex)}
                          aria-label={`Remove axis ${axisIndex + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Label>Source rating questions</Label>
                        <Button
                          type="button"
                          variant="secondary"
                          size="auto"
                          onClick={() => {
                            setActiveSourceAxis(activeSourceAxis === axisIndex ? null : axisIndex);
                            setSourceSearch('');
                          }}
                        >
                          Add source question
                        </Button>
                      </div>
                      {axis.source_questions.length === 0 ? (
                        <p className="text-xs text-destructive">No source questions selected.</p>
                      ) : (
                        <div className="space-y-2">
                          {axis.source_questions.map((question) => (
                            <div
                              key={question.key}
                              className="flex items-start justify-between gap-3 rounded-lg border border-border/40 bg-white/50 px-3 py-2 text-sm"
                            >
                              <div className="min-w-0">
                                <p className="font-mono text-xs">{question.key}</p>
                                <p className="truncate">{question.text}</p>
                                <p className="text-xs text-muted-foreground">
                                  {question.is_active ? 'Active' : 'Inactive'}
                                  {question.flows.length > 0
                                    ? ` · ${question.flows.map((flow) => flow.flow_name).join(', ')}`
                                    : ' · No modules'}
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeSourceQuestion(axisIndex, question.key)}
                                aria-label={`Remove source ${question.key}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {activeSourceAxis === axisIndex ? (
                      <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input
                            aria-label={`Search source questions for axis ${axisIndex + 1}`}
                            value={sourceSearch}
                            onChange={(event) => setSourceSearch(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') void fetchSourceCandidates();
                            }}
                            placeholder="Search rating questions by key or text"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            size="auto"
                            onClick={() => void fetchSourceCandidates()}
                          >
                            Search
                          </Button>
                        </div>
                        {sourceLoading ? (
                          <LoadingAnimation size="sm" />
                        ) : (
                          <div className="max-h-64 space-y-2 overflow-y-auto">
                            {sourceCandidates.map((question) => {
                              const isMapped = mappedQuestionKeys.has(question.key);
                              return (
                                <div
                                  key={question.key}
                                  className="flex items-start justify-between gap-3 rounded-lg bg-white/70 px-3 py-2 text-sm"
                                >
                                  <div className="min-w-0">
                                    <p className="font-mono text-xs">{question.key}</p>
                                    <p className="truncate">{question.text}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {question.is_active === false ? 'Inactive' : 'Active'}
                                      {(question.flows ?? []).length > 0
                                        ? ` · ${(question.flows ?? []).map((flow) => flow.flow_name).join(', ')}`
                                        : ' · No modules'}
                                    </p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="auto"
                                    disabled={isMapped}
                                    onClick={() => addSourceQuestion(axisIndex, question)}
                                  >
                                    {isMapped ? 'Mapped' : 'Add'}
                                  </Button>
                                </div>
                              );
                            })}
                            {sourceCandidates.length === 0 ? (
                              <p className="text-xs text-muted-foreground">No rating questions found.</p>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-border/50 p-4">
                  <h3 className="font-semibold">Readiness</h3>
                  {readinessIssues.length === 0 ? (
                    <p className="mt-2 text-sm text-green-700">Ready to save.</p>
                  ) : (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-destructive">
                      {readinessIssues.map((issue) => <li key={issue}>{issue}</li>)}
                    </ul>
                  )}
                </div>
                <div className="rounded-xl border border-border/50 p-4">
                  <h3 className="font-semibold">Radar profile preview</h3>
                  <ol className="mt-2 space-y-1 text-sm">
                    {draft.axes.map((axis, index) => (
                      <li key={`${axis.key}-${index}`}>
                        {index + 1}. {axis.label || 'Untitled axis'} · {axis.source_questions.length}{' '}
                        source{axis.source_questions.length === 1 ? '' : 's'}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="button" size="auto" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save radar profile'}
                </Button>
                {selectedKey && selectedKey !== SYSTEM_DEFINITION_KEY && draft.is_active ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="auto"
                    onClick={handleArchive}
                    disabled={saving}
                    className="text-red-700 hover:bg-red-50 hover:text-red-800"
                  >
                    <Archive className="mr-2 h-4 w-4" /> Archive
                  </Button>
                ) : null}
              </div>
              {selectedKey ? (
                <section className="space-y-3 border-t border-border/50 pt-6">
                  <h3 className="font-semibold text-foreground">Change history</h3>
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No revisions recorded.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {history.map((revision) => (
                      <li key={revision.version} className="rounded-lg border border-border/40 px-3 py-2">
                        Version {revision.version} · {revision.created_by ?? 'System'} ·{' '}
                        {new Date(revision.created_at).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                )}
                </section>
              ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
